import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/format";

const toNumber = (v) => Number(String(v).replace(",", "."));

const ProductRow = ({ product }) => {
  const qc = useQueryClient();
  const [price, setPrice] = useState(String(product.price));
  const [stock, setStock] = useState(String(product.stock));
  const [active, setActive] = useState(product.active);
  const [saving, setSaving] = useState(false);
  const dirty = toNumber(price) !== product.price || parseInt(stock, 10) !== product.stock || active !== product.active;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/products/${product.slug}`, { price: toNumber(price), stock: parseInt(stock, 10), active });
      toast.success(`${product.name} aggiornato`);
      if (data?.alerts_queued > 0) toast.success(`Avviso "di nuovo disponibile" in invio a ${data.alerts_queued} clienti`);
      await Promise.all(["admin-products", "products"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      qc.invalidateQueries({ queryKey: ["product", product.slug] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const input = "h-12 w-28 rounded-xl border border-navy/20 bg-ivory px-3 text-[16px] text-navy outline-none focus:border-copper";
  return (
    <tr data-testid={`admin-product-row-${product.slug}`}>
      <td className="px-5 py-4">
        <div className="flex items-center gap-4">
          <img src={product.image} alt="" className="h-16 w-[3.2rem] rounded-lg bg-parchment object-cover" />
          <div>
            <p className="font-display text-xl text-navy">{product.name}</p>
            <p className="text-[13px] text-slate2">{product.category} · {product.size}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-4"><input aria-label={`Prezzo ${product.name}`} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className={input} data-testid={`admin-price-${product.slug}`} /></td>
      <td className="px-5 py-4"><input aria-label={`Giacenza ${product.name}`} inputMode="numeric" value={stock} onChange={(e) => setStock(e.target.value)} className={input} data-testid={`admin-stock-${product.slug}`} /></td>
      <td className="px-5 py-4"><Switch checked={active} onCheckedChange={setActive} aria-label="Visibile nel negozio" data-testid={`admin-active-${product.slug}`} /></td>
      <td className="px-5 py-4 text-center font-display text-xl text-navy" data-testid={`admin-alerts-${product.slug}`}>{product.alerts_pending ?? 0}</td>
      <td className="px-5 py-4">
        <button type="button" disabled={!dirty || saving} onClick={save} data-testid={`admin-save-${product.slug}`} className="h-11 rounded-full bg-navy px-6 text-[15px] font-medium text-ivory transition-opacity disabled:opacity-30">
          {saving ? "Salvo…" : "Salva"}
        </button>
      </td>
    </tr>
  );
};

export const ProductsTable = ({ products }) => (
  <div className="overflow-x-auto rounded-3xl border border-copper/20 bg-cream" data-testid="admin-products-table">
    <table className="w-full min-w-[860px] text-left text-[15px]">
      <thead className="border-b border-copper/20 text-[13px] uppercase tracking-[0.15em] text-slate2">
        <tr>{["Prodotto", "Prezzo (€)", "Giacenza", "Visibile", "Avvisi in attesa", ""].map((h) => <th key={h} className="px-5 py-4 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-copper/10">
        {products.map((p) => <ProductRow key={p.slug} product={p} />)}
      </tbody>
    </table>
  </div>
);
